<template>
  <Page>
    <div
      class="d-flex flex-row flex-wrap align-items-center pb-0 mb-3 justify-content-between page-header-content"
    >
      <div
        class="d-flex w-full justify-end flex-wrap datatable-report-actions mb-4"
      >
        <BatchSummary :batch="batch" />
      </div>

      <DatatableReportV3
        v-if="config"
        id="batch_details_report"
        ref="datatable"
        :source="source"
        :config="config"
        :filters="filters"
        :map-row-class="handleMapRowClass"
        :title="`Batch ${id} Details`"
        subpage
        hide-global-filters
        disable-filter-param-update
        no-download
      />
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable";

import Page from "@/components/Page.vue";
import VueChannel from "@/channels/VueChannel";
import BatchItemExportStatus from "@/Reports/BatchItemExportStatus.vue";
import BatchSummary from "@/Reports/BatchSummary.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import { Batch } from "@/typings/batch";

const props = withDefaults(
  defineProps<{
    source?: string;
    summary?: string;
    filters?: any[];
    id?: number;
  }>(),
  {
    source: "",
    summary: "",
    filters: () => [],
    id: 0
  }
);

const store = useStore();
const config = ref<DatatableConfig | null>(null);
const tenant = computed(() => store.state.tenant);
const fetchingTenant = computed(() => store.state.fetchingTenant);
const batches = computed(() => store.state.batches);

const batch = computed((): Batch | null => {
  if (props.id in batches.value) {
    return batches.value[props.id];
  }
  return null;
});

const channel = new VueChannel();

const init = () => {
  setConfig();
  listenForCableUpdates();
};

const setConfig = (): void => {
  config.value = {
    order: [[2, "asc"]],
    columns: [
      {
        data: "record_type",
        title: "Type",
        filterName: "record_type",
        orderable: true
      },
      {
        data: "item_type",
        title: "Object Type",
        filterName: "item_type",
        orderable: true
      },
      {
        data: "invoice_id",
        title: "Invoice",
        orderable: true
      },
      {
        data: "date",
        title: "Invoice Date",
        filterName: "finalized_at",
        orderable: true
      },
      {
        data: "export_status",
        title: "Export Status",
        filterName: "export_status",
        orderable: true
      },
      { data: "job" },
      { data: "customer" },
      { data: "customer_email", title: "Email" },
      { data: "customer_phone", title: "Phone" },
      {
        data: "service_address",
        orderable: false
      },
      { data: "department", filterName: "department_id" }
    ],
    vueComponents: [
      {
        rootId: "export-status-vue",
        component: BatchItemExportStatus,
        options: {
          store
        }
      }
    ]
  };
};

async function listenForCableUpdates() {
  try {
    await cable.subscribe(channel);
  } catch (err) {
    console.log("App Unable to join Cable server", err);
  }
}

interface RowClassObject {
  [className: string]: boolean;
}

interface BatchItemData {
  export_status_value?: string;
}

interface DatatableColumn {
  data: string;
  title?: string;
  filterName?: string;
  orderable?: boolean;
}

interface DatatableConfig {
  order: [number, string][];
  columns: DatatableColumn[];
  vueComponents: {
    rootId: string;
    component: any;
    options: any;
  }[];
}

const createRowClassObject = (data: BatchItemData): RowClassObject => {
  return {
    failed: data.export_status_value === "failed"
  };
};

const applyRowClass = (row: Element, rowClassObject: RowClassObject): void => {
  for (const className in rowClassObject) {
    if (rowClassObject[className]) {
      row.classList.add(className);
    } else {
      row.classList.remove(className);
    }
  }
};

const handleMapRowClass = (params: any): void => {
  const rowClassObj = createRowClassObject(params.data);
  applyRowClass(params.row.node(), rowClassObj);
};

watch(
  tenant,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      init();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (!tenant.value && !fetchingTenant.value) {
    await store.dispatch("fetchTenant");
  }

  store.dispatch("addBreadcrumb", {
    label: "Batches",
    url: "/accounting/batches"
  });
  store.dispatch("addBreadcrumb", { label: `Batch ${props.id} Details` });
});
</script>

<style scoped lang="scss">
.batch-summary-stats {
  background-color: var(--angel-blue);
}

::v-deep .datatable-v2 .global-filters {
  z-index: unset;
}

::v-deep .batch-type-filter-width {
  min-width: 90px;
}

:deep(tr.failed.even:nth-child(2n)) {
  background-color: var(--sera-color-accents-red-100) !important;
}

:deep(tr.failed.odd) {
  background-color: var(--sera-color-accents-red-100) !important;
}
</style>
